import { useEffect, useRef, useState } from 'react'
import { FORMATOS } from './scanner-formatos'
import type { CasoDeUso } from './scanner-formatos'

/**
 * O leitor de código pela câmera.
 *
 * ## Por que uma biblioteca, e não a API do navegador
 *
 * `BarcodeDetector` é a API padrão e o **Safari não a implementa** — nem no
 * iPhone, que é o alvo deste app. Foi por isso que a Fase 7 adiou o leitor; o
 * que faltava naquela leitura é que `BarcodeDetector` não é o único caminho.
 * `getUserMedia` funciona no Safari e dentro de PWA instalado desde o iOS 14.3,
 * e uma biblioteca WASM lê os quadros.
 *
 * ## O peso, medido antes de aceitar a dependência
 *
 * O WASM do leitor tem 1.068 KB brutos — **445 KB em gzip, 349 em brotli**, que
 * é o que de fato trafega. Ele **não entra no bundle**: o `import()` é dinâmico,
 * então o Vite corta num chunk à parte, e quem nunca abre o leitor não baixa
 * nada. E `globPatterns` do PWA não inclui `wasm`, então ele também não entra no
 * precache da instalação — o que seria o mesmo problema por outro caminho.
 * Depois do primeiro uso, o `Cache-Control` imutável de `/assets/**` resolve.
 *
 * ## A câmera é desligada no cleanup, e isso não é detalhe
 *
 * Uma track de vídeo que fica aberta mantém a luz da câmera acesa e come
 * bateria, mesmo com o componente fora da tela. O `return` do efeito para as
 * tracks — é a única coisa aqui que, se faltar, ninguém reporta como bug e todo
 * mundo sente.
 *
 * ## Lê um quadro por vez, e não o vídeo inteiro
 *
 * O quadro é desenhado num canvas de 640 px de largura antes de ir para o
 * decodificador. Mandar 1080p a 30 fps travaria o celular sem ler melhor: código
 * de barras é preto e branco e grande, e resolução demais só aumenta o trabalho.
 */

/** Um quadro a cada 120 ms: rápido o bastante para parecer instantâneo, e
 * folgado o bastante para não fritar o celular. */
const INTERVALO_MS = 120
const LARGURA = 640

type Estado = 'abrindo' | 'lendo' | 'negado' | 'semCamera' | 'falhou'

export function Scanner({
  caso,
  onLer,
  onFechar,
}: {
  caso: CasoDeUso
  onLer: (texto: string) => void
  onFechar: () => void
}) {
  const video = useRef<HTMLVideoElement>(null)
  const [estado, setEstado] = useState<Estado>('abrindo')

  useEffect(() => {
    let vivo = true
    let stream: MediaStream | null = null
    let timer: ReturnType<typeof setTimeout> | null = null

    async function comecar() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // A traseira. Sem isto, o celular abre a frontal e a pessoa aponta o
          // cupom para o próprio rosto.
          video: { facingMode: { ideal: 'environment' } },
        })
        if (!vivo) return
        const elemento = video.current
        if (elemento === null) return
        elemento.srcObject = stream
        // `playsInline` no elemento e `play()` aqui: o iOS não toca vídeo
        // inline sem os dois, e sem tocar não há quadro para ler.
        await elemento.play()

        // Só agora o WASM é baixado — depois de a câmera abrir, para os 445 KB
        // e a permissão não disputarem o mesmo instante.
        const { readBarcodes, prepareZXingModule } = await import('zxing-wasm/reader')
        const wasm = (await import('zxing-wasm/reader/zxing_reader.wasm?url')).default
        // Servido da nossa origem, e não do CDN que a biblioteca usa por
        // padrão: um app que depende de jsDelivr para escanear para de
        // funcionar quando o jsDelivr para.
        prepareZXingModule({ overrides: { locateFile: () => wasm }, fireImmediately: false })
        if (!vivo) return
        setEstado('lendo')

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (ctx === null) {
          setEstado('falhou')
          return
        }

        const ler = async () => {
          if (!vivo || elemento.videoWidth === 0) {
            if (vivo) timer = setTimeout(() => void ler(), INTERVALO_MS)
            return
          }
          const escala = LARGURA / elemento.videoWidth
          canvas.width = LARGURA
          canvas.height = Math.round(elemento.videoHeight * escala)
          ctx.drawImage(elemento, 0, 0, canvas.width, canvas.height)

          try {
            const achados = await readBarcodes(
              ctx.getImageData(0, 0, canvas.width, canvas.height),
              { formats: [...FORMATOS[caso]], tryHarder: true, maxNumberOfSymbols: 1 },
            )
            const texto = achados[0]?.text
            if (vivo && texto !== undefined && texto !== '') {
              onLer(texto)
              return
            }
          } catch (erro: unknown) {
            // Um quadro que não decodifica é o caso normal — a maioria dos
            // quadros não tem código nenhum. Só o console fica sabendo.
            console.debug('Scanner: quadro sem leitura', erro)
          }
          if (vivo) timer = setTimeout(() => void ler(), INTERVALO_MS)
        }
        void ler()
      } catch (erro: unknown) {
        if (!vivo) return
        // Os três desfechos que a pessoa pode resolver, separados: cada um pede
        // uma ação diferente, e "não deu" não diz qual.
        const nome = erro instanceof Error ? erro.name : ''
        if (nome === 'NotAllowedError') setEstado('negado')
        else if (nome === 'NotFoundError' || nome === 'OverconstrainedError') setEstado('semCamera')
        else {
          console.error('Scanner: não deu para abrir a câmera', erro)
          setEstado('falhou')
        }
      }
    }

    void comecar()

    return () => {
      vivo = false
      if (timer !== null) clearTimeout(timer)
      // A câmera desligada. Ver o cabeçalho.
      stream?.getTracks().forEach((t) => {
        t.stop()
      })
    }
  }, [caso, onLer])

  return (
    <div className="mt-2">
      <div className="relative overflow-hidden rounded-lg bg-black">
        <video
          ref={video}
          playsInline
          muted
          className="h-56 w-full object-cover"
          aria-label={caso === 'qr' ? 'Câmera lendo o QR do cupom' : 'Câmera lendo o código de barras'}
        />
        {/* A mira: o código não precisa preencher a tela, mas precisa estar
            dentro dela e reto. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-8 inset-y-[38%] rounded border-2 border-white/70"
        />
      </div>

      <p role="status" className="mt-2 text-xs text-[var(--text-muted)]">
        {MENSAGEM[estado]}
      </p>

      <button type="button" className="mt-1 text-xs underline" onClick={onFechar}>
        Fechar a câmera
      </button>
    </div>
  )
}

const MENSAGEM: Record<Estado, string> = {
  abrindo: 'Abrindo a câmera…',
  lendo: 'Aponte para o código.',
  negado: 'A câmera foi bloqueada. Libere nas permissões do site e tente de novo.',
  semCamera: 'Não achei uma câmera traseira neste aparelho.',
  falhou: 'Não deu para abrir a câmera. Dá para digitar o código.',
}
